import React from 'react';
import { Shield, Lock, Eye, Database, UserCheck, Mail, Settings } from 'lucide-react';

const PrivacyPage = () => {
  const sections = [
    {
      id: 'collection',
      title: 'Information We Collect',
      icon: Database,
      content: [
        {
          subtitle: 'Account Information',
          text: 'When you create an account, we collect your name, email address, phone number, company name, MC/DOT numbers, and role (shipper, broker, carrier, owner-operator, or dispatcher).'
        },
        {
          subtitle: 'Verification Documents',
          text: 'To verify your identity and business credentials, we may collect copies of your insurance certificates, operating authority documents, and other relevant business licenses.'
        },
        {
          subtitle: 'Load and Transaction Data',
          text: 'We collect information about loads you post, request, or transport, including pickup/delivery locations, vehicle details, pricing, and status updates.'
        },
        {
          subtitle: 'Usage Information',
          text: 'We automatically collect information about how you use our platform, including IP addresses, browser type, device information, and pages visited.'
        }
      ]
    },
    {
      id: 'use',
      title: 'How We Use Your Information',
      icon: Settings,
      content: [
        {
          subtitle: 'Platform Operations',
          text: 'We use your information to provide, maintain, and improve our services, process transactions, and facilitate connections between shippers, brokers, and carriers.'
        },
        {
          subtitle: 'Verification & Trust',
          text: 'We use your documents and business information to verify your identity and credentials, maintaining the integrity of our verified platform.'
        },
        {
          subtitle: 'Communication',
          text: 'We use your contact information to send service updates, notifications about load activity, and important platform announcements.'
        },
        {
          subtitle: 'Safety & Fraud Prevention',
          text: 'We analyze account activity and user reports to detect, prevent, and address fraud, abuse, and security issues.'
        }
      ]
    },
    {
      id: 'sharing',
      title: 'Information Sharing',
      icon: UserCheck,
      content: [
        {
          subtitle: 'With Other Users',
          text: 'When you post loads or request to haul, relevant information (company name, ratings, verification status) is shared with other platform users to facilitate business transactions.'
        },
        {
          subtitle: 'Service Providers',
          text: 'We share information with third-party service providers who help us operate our platform, including payment processors, email services, and cloud hosting providers.'
        },
        {
          subtitle: 'Legal Requirements',
          text: 'We may disclose information when required by law, to protect our rights, or to ensure the safety of our users and the public.'
        },
        {
          subtitle: 'What We Never Do',
          text: 'We never sell your personal information to third parties for marketing purposes.'
        }
      ]
    },
    {
      id: 'security',
      title: 'Data Security',
      icon: Lock,
      content: [
        {
          subtitle: 'Encryption',
          text: 'All data transmitted between your browser and our servers is encrypted using industry-standard TLS/SSL encryption.'
        },
        {
          subtitle: 'Access Controls',
          text: 'We implement strict access controls and authentication measures to protect your account and personal information.'
        },
        {
          subtitle: 'Secure Storage',
          text: 'Your data is stored in secure, SOC 2 compliant data centers with regular backups and disaster recovery procedures.'
        },
        {
          subtitle: 'Two-Factor Authentication',
          text: 'We offer two-factor authentication (2FA) to add an extra layer of security to your account.'
        }
      ]
    },
    {
      id: 'rights',
      title: 'Your Rights',
      icon: Eye,
      content: [
        {
          subtitle: 'Access & Portability',
          text: 'You can access and download your personal data at any time through your account settings.'
        },
        {
          subtitle: 'Correction',
          text: 'You can update or correct your personal information through your profile settings.'
        },
        {
          subtitle: 'Deletion',
          text: 'You can request deletion of your account and personal data by contacting our support team. Note that we may retain certain information as required by law or for legitimate business purposes.'
        },
        {
          subtitle: 'Opt-Out',
          text: 'You can opt out of marketing communications at any time by clicking the unsubscribe link in our emails or updating your notification preferences.'
        }
      ]
    },
    {
      id: 'contact',
      title: 'Contact Us',
      icon: Mail,
      content: [
        {
          subtitle: 'Privacy Questions',
          text: 'If you have questions about this Privacy Policy or our data practices, please contact us at support@haullogixloads.com or call (313) 314-2922.'
        },
        {
          subtitle: 'Data Requests',
          text: 'For data access, correction, or deletion requests, please email support@haullogixloads.com with the subject line "Privacy Request".'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0c10] pt-16">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#4ADE80]/10 to-transparent py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-['Barlow_Condensed'] uppercase tracking-wide">
            Privacy <span className="text-[#4ADE80]">Policy</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </p>
          <p className="text-sm text-gray-500 mt-4">Last updated: March 14, 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Introduction */}
        <div className="bg-[#12151a] rounded-xl border border-[#1a1d24] p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-[#4ADE80]" />
            <h2 className="text-xl font-bold text-white">Our Commitment to Privacy</h2>
          </div>
          <p className="text-gray-300 leading-relaxed">
            HaulLogix ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy 
            describes how we collect, use, disclose, and safeguard your information when you use our 
            website and services at haullogixloads.com. By using HaulLogix, you agree to the collection 
            and use of information in accordance with this policy.
          </p>
        </div>

        {/* Table of Contents */}
        <div className="bg-[#12151a] rounded-xl border border-[#1a1d24] p-6 mb-8">
          <h3 className="text-lg font-semibold text-white mb-4">Contents</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {sections.map((section) => (
              <a 
                key={section.id}
                href={`#${section.id}`}
                className="text-[#4ADE80] hover:underline text-sm"
              >
                {section.title}
              </a>
            ))}
          </div>
        </div>

        {/* Sections */}
        {sections.map((section) => (
          <section key={section.id} id={section.id} className="mb-8">
            <div className="bg-[#12151a] rounded-xl border border-[#1a1d24] p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center">
                  <section.icon className="w-5 h-5 text-[#4ADE80]" />
                </div>
                <h2 className="text-xl font-bold text-white">{section.title}</h2>
              </div>
              <div className="space-y-6">
                {section.content.map((item, i) => (
                  <div key={i}>
                    <h3 className="font-semibold text-white mb-2">{item.subtitle}</h3>
                    <p className="text-gray-400 leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ))}

        {/* Footer Note */}
        <div className="text-center text-gray-500 text-sm">
          <p>
            This Privacy Policy is effective as of March 14, 2026 and will remain in effect except 
            with respect to any changes in its provisions in the future.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
